import { useState, useEffect } from "react";
import type { RefObject } from "react";

/**
 * Returns true when the element pointed to by ref is intersecting the viewport.
 * Uses the given threshold (0–1) for how much of the element must be visible.
 *
 * @param ref - Ref object for the element to observe (e.g. useRef<HTMLDivElement>(null)).
 * @param threshold - Fraction of element that must be in view (default 1 = 100%).
 * @returns Whether the element is currently visible per the threshold.
 */
export default function useIsVisible(
  ref: RefObject<HTMLElement | null>,
  threshold: number = 1,
): boolean {
  const [isIntersecting, setIntersecting] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry) {
          setIntersecting(entry.isIntersecting);
        }
      },
      { threshold },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, threshold]);

  return isIntersecting;
}
